// Shared helpers for detecting moves and rotation direction from drag paths
// Ported from ios/MoveDetection.swift

import type { Move, BoardTopology } from '../../shared/types'
import type { RingenGame } from '../../shared/RingenGame'

export interface TilePosition {
  row: number
  col: number
}

export interface MoveMatch {
  moveIndices: number[]
  clockwise: boolean
  dynamicCycle?: number[] // ouroboros: compact indices for the dynamic cycle
}

/**
 * Find matching moves from a drag path
 * Returns move indices and inferred clockwise direction
 */
export function findMatchingMoves(
  tiles: TilePosition[],
  moves: Move[],
  game: RingenGame,
  topology: BoardTopology = 'rect',
  allowOuroboros: boolean = false,
): MoveMatch {
  if (tiles.length < 2) {
    return { moveIndices: [], clockwise: true }
  }

  // Convert dragged tiles to full board indices
  const fullIndices = tiles.map((t) => t.row * game.cols + t.col)
  const draggedIndices = new Set(fullIndices)
  const matchingMoveIndices: number[] = []

  // Check each predefined move using FULL indices
  for (let moveIndex = 0; moveIndex < moves.length; moveIndex++) {
    const move = moves[moveIndex]
    const moveFullIndices = new Set(move.cycle.map((compact) => game.fullIndex(compact)))

    // Check if dragged indices are a subset of this move's cycle
    if (isSubset(draggedIndices, moveFullIndices)) {
      matchingMoveIndices.push(moveIndex)
    }
  }

  // Determine direction from first two tiles in drag for the first match
  let clockwise = true
  if (matchingMoveIndices.length > 0) {
    clockwise = inferClockwiseDirection(tiles, moves[matchingMoveIndices[0]], game)
  }

  if (matchingMoveIndices.length > 0) {
    return { moveIndices: matchingMoveIndices, clockwise }
  }

  // Try ouroboros if no predefined moves matched
  if (allowOuroboros && topology !== 'graph') {
    const dynamic = validateOuroborosPath(fullIndices, topology, game)
    if (dynamic) {
      return { moveIndices: [], clockwise, dynamicCycle: dynamic }
    }
  }

  return { moveIndices: matchingMoveIndices, clockwise }
}

/**
 * Infer rotation direction for a specific move from the drag order
 */
export function inferClockwiseDirection(
  tiles: TilePosition[],
  move: Move,
  game: RingenGame
): boolean {
  if (tiles.length < 2) return true

  // Convert compact indices to full board indices
  const moveFullIndices = move.cycle.map((compact) => game.fullIndex(compact))

  // Find first two dragged tiles that are part of this move
  let firstFullIndex: number | null = null
  let secondFullIndex: number | null = null

  for (const tile of tiles) {
    const fullIndex = tile.row * game.cols + tile.col
    if (moveFullIndices.includes(fullIndex)) {
      if (firstFullIndex === null) {
        firstFullIndex = fullIndex
      } else if (secondFullIndex === null) {
        secondFullIndex = fullIndex
        break
      }
    }
  }

  if (firstFullIndex === null || secondFullIndex === null) return true

  const firstPos = moveFullIndices.indexOf(firstFullIndex)
  const secondPos = moveFullIndices.indexOf(secondFullIndex)

  if (firstPos === -1 || secondPos === -1) return true

  // If the second position advances by one (wrapping), treat as clockwise
  return secondPos === (firstPos + 1) % moveFullIndices.length
}

/**
 * Validate an ouroboros drag path: must form a closed loop of adjacent tiles
 * Returns compact indices for the dynamic cycle, or null if invalid
 */
function validateOuroborosPath(
  fullIndices: number[],
  topology: BoardTopology,
  game: RingenGame,
): number[] | null {
  if (fullIndices.length < 4) return null
  // Path must close on itself (first == last)
  if (fullIndices[0] !== fullIndices[fullIndices.length - 1]) return null

  const path = fullIndices.slice(0, -1) // Drop the closing duplicate
  if (path.length < 3) return null
  // No repeated tiles in the path
  if (new Set(path).size !== path.length) return null

  const width = game.cols

  // Check adjacency along the path
  for (let i = 0; i < path.length - 1; i++) {
    if (!areAdjacent(path[i], path[i + 1], topology, width)) {
      return null
    }
  }
  // Check adjacency of last to first (closing the loop)
  if (!areAdjacent(path[path.length - 1], path[0], topology, width)) {
    return null
  }

  // Convert full indices to compact indices
  return path.map((fullIdx) => game.compactIndex(fullIdx)).filter((i) => i !== -1)
}

function areAdjacent(a: number, b: number, topology: BoardTopology, width: number): boolean {
  const rowA = Math.floor(a / width)
  const colA = a % width
  const rowB = Math.floor(b / width)
  const colB = b % width
  const dr = Math.abs(rowA - rowB)
  const dc = Math.abs(colA - colB)

  switch (topology) {
    case 'rect':
      return (dr === 1 && dc === 0) || (dr === 0 && dc === 1)
    case 'hex_odd_r':
    case 'hex_odd_q':
      return dr <= 1 && dc <= 1 && !(dr === 0 && dc === 0)
    case 'graph':
      return false
  }
}

/**
 * Check if setA is a subset of setB
 */
function isSubset<T>(setA: Set<T>, setB: Set<T>): boolean {
  for (const elem of setA) {
    if (!setB.has(elem)) {
      return false
    }
  }
  return true
}
